import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDayBookReturn } from "@/lib/useDayBookReturn";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { format } from "date-fns";
import { Plus, Pencil, Trash, DownloadSimple, UploadSimple, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { isValidGstin, lookupGstin, normalizeGstin } from "@/lib/gstinLookup";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { GstBreakdown } from "@/components/GstBreakdown";
import { CustomerCombobox } from "@/components/InvoiceCombos";
import { ProductPicker } from "@/components/ProductPicker";
import { useFieldCfg } from "@/lib/softwareConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal"];
const STATE_CODES = {"Andhra Pradesh":"37","Bihar":"10","Delhi":"07","Gujarat":"24","Haryana":"06","Karnataka":"29","Kerala":"32","Madhya Pradesh":"23","Maharashtra":"27","Punjab":"03","Rajasthan":"08","Tamil Nadu":"33","Telangana":"36","Uttar Pradesh":"09","West Bengal":"19"};
const GST_RATES = [0, 5, 12, 18, 28];

const emptyForm = { supplier_name: "", supplier_gstin: "", supplier_address: "", supplier_state: "Maharashtra", supplier_state_code: "27", invoice_no: "", invoice_date: new Date().toISOString().split("T")[0], gst_type: "cgst_sgst", notes: "", to_godown: false, godown_id: "", godown_name: "" };
const emptyItem = { product_id: "", description: "", hsn_sac: "", quantity: 1, price: 0, gst_rate: 18, amount: 0, unit: "PCS" };

export default function Purchases() {
  const fcfg = useFieldCfg("purchase");
  useDayBookReturn();
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({...emptyForm});
  const [items, setItems] = useState([]);
  const [godowns, setGodowns] = useState([]);
  useEffect(() => { axios.get(`${API}/godowns`).then((r) => setGodowns(r.data.items || [])).catch(() => {}); }, [dialogOpen]);
  const [summary, setSummary] = useState(null);
  // Financial Year navigation (Apr → Mar). fyStart=2025 means FY 2025-26.
  // Default to the current running FY.
  const [fyStart, setFyStart] = useState(() => {
    const d = new Date();
    return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  });
  const [tab, setTab] = useState("list"); // list | report
  const [search, setSearch] = useState("");
  const [selIds, setSelIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ supplier_name: "", invoice_date: "" });
  const [supplierQuery, setSupplierQuery] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  // ---- Debit / Credit Note against a purchase ----------------------------
  // Modal state: null when closed. Object `{ purchase, noteType }` when open.
  const [noteModal, setNoteModal] = useState(null);
  const [noteForm, setNoteForm] = useState({
    note_date: new Date().toISOString().slice(0, 10), reason: "", notes: "", items: [],
  });
  const [noteSaving, setNoteSaving] = useState(false);

  const openNoteModal = (purchase, noteType) => {
    // Seed items from the source purchase so the user usually just tweaks
    // quantities/prices instead of re-typing every line from scratch.
    setNoteForm({
      note_date: new Date().toISOString().slice(0, 10),
      reason: noteType === "debit" ? "Goods returned to supplier" : "Discount / shortage credit received",
      notes: "",
      items: (purchase.items || []).map((it) => ({
        product_id: it.product_id || "",
        product_name: it.description || it.product_name || "",
        hsn_sac: it.hsn_sac || "",
        quantity: Number(it.quantity || 0),
        price: Number(it.price || 0),
        gst_rate: Number(it.gst_rate || 0),
        amount: Number(it.amount || 0),
        unit: (it.unit || "PCS").toUpperCase(),  // carry the unit exactly as recorded on the source bill
      })),
    });
    setNoteModal({ purchase, noteType });
  };

  const updateNoteItem = (idx, field, val) => {
    setNoteForm((prev) => {
      const items = [...prev.items];
      const numFields = ["quantity", "price", "gst_rate"];
      items[idx] = { ...items[idx], [field]: numFields.includes(field) ? Number(val) || 0 : val };
      // Auto-recalc amount = qty × price so total tallies right in the note.
      items[idx].amount = Number((items[idx].quantity * items[idx].price).toFixed(2));
      return { ...prev, items };
    });
  };
  const removeNoteItem = (idx) => setNoteForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const noteTotals = useMemo(() => {
    const src = noteModal?.purchase;
    const taxable = noteForm.items.reduce((s, i) => s + Number(i.amount || 0), 0);
    const isCgstSgst = (src?.gst_type || "cgst_sgst") === "cgst_sgst";
    let cgst = 0, sgst = 0, igst = 0;
    noteForm.items.forEach((i) => {
      const tax = Number(i.amount || 0) * Number(i.gst_rate || 0) / 100;
      if (isCgstSgst) { cgst += tax / 2; sgst += tax / 2; } else { igst += tax; }
    });
    return { taxable: +taxable.toFixed(2), cgst: +cgst.toFixed(2), sgst: +sgst.toFixed(2), igst: +igst.toFixed(2), total: +(taxable + cgst + sgst + igst).toFixed(2) };
  }, [noteForm.items, noteModal?.purchase]);

  const submitNote = async () => {
    if (!noteModal) return;
    if (!noteForm.reason.trim()) { alert("Reason is mandatory"); return; }
    if (noteForm.items.length === 0) { alert("At least one item required"); return; }
    setNoteSaving(true);
    try {
      const payload = {
        note_type: noteModal.noteType, note_date: noteForm.note_date,
        reason: noteForm.reason, notes: noteForm.notes || null,
        gst_type: noteModal.purchase.gst_type || "cgst_sgst",
        items: noteForm.items,
      };
      const res = await axios.post(`${API}/purchases/${noteModal.purchase.id}/notes`, payload);
      alert(`${noteModal.noteType === "debit" ? "Debit" : "Credit"} Note ${res.data.note_no} created — stock ledger updated, reports adjusted.`);
      setNoteModal(null);
      fetchPurchases();
    } catch (e) {
      alert("Failed to save note: " + (e.response?.data?.detail || e.message));
    } finally { setNoteSaving(false); }
  };
  const [pMonth, setPMonth] = useState("");
  const [pFrom, setPFrom] = useState("");
  const [pTo, setPTo] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importRef = useRef(null);
  const [gstinMsg, setGstinMsg] = useState(null);
  const [buyerStateCode, setBuyerStateCode] = useState("");

  // Load own company GSTIN prefix once to derive intra/inter-state auto-selection
  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => {
      const s = r.data || {};
      setBuyerStateCode((s.state_code || (s.gstin || "").slice(0, 2) || "").toString());
    }).catch(() => {});
  }, []);

  // Auto pick gst_type when supplier state changes (skip if user chose "no_gst" — Tax Free)
  const applyAutoGstType = (supplierStateCode) => {
    const a = (buyerStateCode || "").trim().padStart(2, "0").slice(0, 2);
    const b = (supplierStateCode || "").trim().padStart(2, "0").slice(0, 2);
    if (!a || !b || a === "00" || b === "00") return;
    setForm((f) => (f.gst_type === "no_gst" ? f : { ...f, gst_type: a === b ? "cgst_sgst" : "igst" }));
  };

  const onSupplierGstinChange = async (raw) => {
    const g = normalizeGstin(raw);
    setForm((f) => ({ ...f, supplier_gstin: g }));
    if (g.length < 15) { setGstinMsg(null); return; }
    if (!isValidGstin(g)) { setGstinMsg({ tone: "text-rose-600", text: "Invalid GSTIN format" }); return; }
    setGstinMsg({ tone: "text-blue-600", text: "Fetching from GST records…" });
    try {
      const d = await lookupGstin(g);
      if (d.found) {
        setForm((f) => ({
          ...f,
          supplier_name: d.name || f.supplier_name,
          supplier_address: d.address || f.supplier_address,
          supplier_state: d.state || f.supplier_state,
          supplier_state_code: d.state_code || f.supplier_state_code,
        }));
        applyAutoGstType(d.state_code);
        setGstinMsg({ tone: "text-emerald-700", text: `✓ ${d.name}${d.status ? ` (${d.status})` : ""} — auto-filled` });
      } else {
        setForm((f) => ({ ...f, supplier_state: d.state || f.supplier_state, supplier_state_code: d.state_code || f.supplier_state_code }));
        applyAutoGstType(d.state_code);
        setGstinMsg({ tone: "text-amber-700", text: d.message || "Only state auto-filled" });
      }
    } catch {
      setGstinMsg({ tone: "text-rose-600", text: "Lookup failed" });
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (importRef.current) importRef.current.value = "";
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/purchase-import/excel`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(r.data);
      fetchPurchases(); fetchProducts();
    } catch (err) {
      setImportResult({ imported: 0, skipped_duplicates: 0, suppliers_created: 0, products_created: 0, errors: [err?.response?.data?.detail || "Import failed"] });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/purchase-import/template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "Purchase_Import_Template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Could not download template"); }
  };

  const visiblePurchases = useMemo(() => {
    const q = search.trim().toLowerCase();
    return purchases.filter((p) => {
      const d = (p.invoice_date || (p.created_at || "").slice(0, 10) || "").slice(0, 10);
      if (pMonth && !d.startsWith(pMonth)) return false;
      if ((pFrom || pTo) && !((!pFrom || d >= pFrom) && (!pTo || d <= pTo))) return false;
      if (q && !(
        (p.supplier_name || "").toLowerCase().includes(q) ||
        (p.invoice_no || "").toLowerCase().includes(q) ||
        (p.purchase_no || "").toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [purchases, pMonth, pFrom, pTo, search]);

  const allVisibleSelected = visiblePurchases.length > 0 && visiblePurchases.every((p) => selIds.has(p.id));
  const toggleSelectAll = () => {
    setSelIds((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) visiblePurchases.forEach((p) => n.delete(p.id));
      else visiblePurchases.forEach((p) => n.add(p.id));
      return n;
    });
  };
  const toggleSel = (id) => {
    setSelIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const submitBulkEdit = async () => {
    if (!bulkForm.supplier_name.trim() && !bulkForm.invoice_date) {
      alert("Fill at least one field (Supplier Name or Date)"); return;
    }
    setBulkSaving(true);
    try {
      const r = await axios.post(`${API}/purchases/bulk-update`, {
        purchase_ids: [...selIds],
        supplier_name: bulkForm.supplier_name.trim() || null,
        invoice_date: bulkForm.invoice_date || null,
      });
      alert(`${r.data.updated} bill(s) updated${r.data.party ? ` · linked to ${r.data.party}` : ""}. Ledger & stock updated automatically.`);
      setBulkOpen(false); setSelIds(new Set()); setBulkForm({ supplier_name: "", invoice_date: "" }); setSupplierQuery("");
      fetchPurchases(); fetchSuppliers();
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    } finally { setBulkSaving(false); }
  };

  const dfv = useDateFilterVersion();
  useEffect(() => { fetchPurchases(); fetchProducts(); fetchSuppliers(); }, [dfv]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === "report") fetchSummary(); }, [fyStart, tab]);

  const fetchPurchases = async () => {
    try { const res = await axios.get(`${API}/purchases`); setPurchases(res.data); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchProducts = async () => {
    try { const res = await axios.get(`${API}/products`); setProducts(res.data); } catch (e) { console.error(e); }
  };

  const fetchSuppliers = async () => {
    try { const res = await axios.get(`${API}/suppliers`); setSuppliers(res.data.suppliers || []); } catch (e) { console.error(e); }
  };

  const fetchSummary = async () => {
    try { const res = await axios.get(`${API}/purchases/report/summary?fy=${fyStart}`); setSummary(res.data); } catch (e) { console.error(e); }
  };

  const addItem = () => setItems([...items, {...emptyItem}]);
  const removeItem = (i) => setItems(items.filter((_,idx) => idx !== i));
  // Direct product object apply (avoids stale products-state lookups when a
  // brand-new product is created inside the picker).
  const applyProduct = (idx, p) => {
    setItems((prev) => {
      const ni = [...prev];
      const row = { ...ni[idx], product_id: p.id, description: p.name,
        hsn_sac: p.hsn_sac || ni[idx].hsn_sac, gst_rate: p.gst_rate ?? ni[idx].gst_rate,
        unit: (p.unit || "PCS").toUpperCase() };
      if (!row.price) row.price = p.price || 0;
      row.amount = (row.quantity || 0) * (row.price || 0);
      ni[idx] = row;
      return ni;
    });
  };
  const updateItem = (i, f, v) => {
    const ni = [...items]; ni[i][f] = v;
    if (f === "quantity" || f === "price") ni[i].amount = ni[i].quantity * ni[i].price;
    // Linking to an existing product auto-fills description, HSN, price &
    // GST so the supplier's bill maps cleanly into stock + ledger.
    if (f === "product_id" && v) {
      const p = products.find((x) => x.id === v);
      if (p) {
        ni[i].description = p.name;
        ni[i].hsn_sac = p.hsn_sac || ni[i].hsn_sac;
        if (!ni[i].price) ni[i].price = p.price;
        ni[i].gst_rate = p.gst_rate;
        ni[i].unit = p.unit || "PCS";
        ni[i].amount = ni[i].quantity * ni[i].price;
      }
    }
    setItems(ni);
  };

  const handleStateChange = (state) => {
    const sc = STATE_CODES[state] || "";
    setForm({...form, supplier_state: state, supplier_state_code: sc});
    applyAutoGstType(sc);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { alert("Add items"); return; }
    try {
      if (editing) { await axios.put(`${API}/purchases/${editing}`, {...form, items}); }
      else { await axios.post(`${API}/purchases`, {...form, items}); }
      setDialogOpen(false); setEditing(null); setForm({...emptyForm}); setItems([]); fetchPurchases();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const handleEdit = (p) => {
    setEditing(p.id);
    setForm({ supplier_name: p.supplier_name, supplier_gstin: p.supplier_gstin || "", supplier_address: p.supplier_address || "", supplier_state: p.supplier_state || "Maharashtra", supplier_state_code: p.supplier_state_code || "", invoice_no: p.invoice_no, invoice_date: p.invoice_date, gst_type: p.gst_type || "cgst_sgst", notes: p.notes || "", to_godown: !!p.to_godown || !!p.godown_id, godown_id: p.godown_id || "", godown_name: p.godown_name || "" });
    setItems(p.items || []);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await axios.delete(`${API}/purchases/${id}`); fetchPurchases(); } catch { alert("Failed"); }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const eid = searchParams.get("edit");
    if (!eid || purchases.length === 0) return;
    const p = purchases.find((x) => x.id === eid);
    if (p) handleEdit(p);
    const ret = searchParams.get("return");
    setSearchParams(ret ? { return: ret } : {}, { replace: true });
  }, [purchases, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setEditing(null); setForm({ ...emptyForm }); setItems([]); setDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGstrExcel = async () => {
    try {
      // Priority: from/to > month > all-time
      const params = new URLSearchParams();
      if (pFrom) params.set("from_date", pFrom);
      if (pTo) params.set("to_date", pTo);
      if (!pFrom && !pTo && pMonth) params.set("month", pMonth);
      const res = await axios.get(`${API}/purchases/report/gstr-excel?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url;
      const slug = pFrom || pTo ? `${pFrom || "start"}_to_${pTo || "end"}` : (pMonth || "all");
      link.setAttribute("download", `Purchases_GSTR_${slug}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("GSTR-style Excel download failed"); }
  };

  const handleTallyXml = async () => {
    try {
      const params = new URLSearchParams();
      if (pFrom) params.set("from_date", pFrom);
      if (pTo) params.set("to_date", pTo);
      if (!pFrom && !pTo && pMonth) params.set("month", pMonth);
      const res = await axios.get(`${API}/purchases/report/tally-xml?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url;
      const slug = pFrom || pTo ? `${pFrom || "start"}_to_${pTo || "end"}` : (pMonth || "all");
      link.setAttribute("download", `Purchases_Tally_${slug}.xml`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.status === 404 ? "No purchase bills found for the selected period" : "Tally XML download failed");
    }
  };

  const handleExport = async (type, month) => {
    try {
      const params = month ? `month=${month}` : `fy=${fyStart}`;
      const slug = month || `FY_${fyStart}-${String((fyStart + 1) % 100).padStart(2, "0")}`;
      const res = await axios.get(`${API}/purchases/report/${type}?${params}`, { responseType: "blob" });
      const ext = type === "excel" ? "xlsx" : "pdf";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `Purchases_${slug}.${ext}`);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { alert("Export failed"); }
  };

  const fmt = (v) => `\u20b9${(v||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  return (
    <div className="p-4 sm:p-8" data-testid="purchases-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-1">Purchases</h1>
          <p className="text-slate-500 text-sm">Manage purchase invoices (separate from sales)</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadTemplate} data-testid="purchase-download-template-btn" className="px-3 py-2 border border-slate-300 text-slate-700 rounded-sm text-sm font-semibold hover:bg-slate-50 flex items-center gap-2">
            <DownloadSimple size={18} weight="bold" /> Download Format
          </button>
          <input ref={importRef} type="file" accept=".xlsx" onChange={handleImportExcel} className="hidden" data-testid="purchase-import-input" />
          <button onClick={() => importRef.current?.click()} disabled={importing} data-testid="purchase-import-btn" className="px-3 py-2 border border-emerald-600 text-emerald-700 rounded-sm text-sm font-semibold hover:bg-emerald-50 flex items-center gap-2 disabled:opacity-50">
            <UploadSimple size={18} weight="bold" /> {importing ? "Importing…" : "Import Excel"}
          </button>
          <button onClick={() => { setEditing(null); setForm({...emptyForm}); setItems([]); setDialogOpen(true); }} data-testid="add-purchase-btn" className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 flex items-center gap-2">
            <Plus size={20} weight="bold" /> Add Purchase
          </button>
        </div>
      </div>

      {importResult && (
        <div className={`mb-6 rounded-sm border px-4 py-3 text-sm ${importResult.imported > 0 ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="purchase-import-result">
          <div className="flex items-center justify-between gap-3">
            <p className="font-semibold">
              Imported <b>{importResult.imported}</b> purchase{importResult.imported === 1 ? "" : "s"}
              {importResult.skipped_duplicates > 0 && <> · <b>{importResult.skipped_duplicates}</b> skipped (duplicates/errors)</>}
              {importResult.suppliers_created > 0 && <> · <b>{importResult.suppliers_created}</b> new suppliers</>}
              {importResult.products_created > 0 && <> · <b>{importResult.products_created}</b> new products</>}
              {importResult.imported > 0 && <> · Stock Ledger updated</>}
            </p>
            <button onClick={() => setImportResult(null)} className="text-xs font-bold underline shrink-0">Dismiss</button>
          </div>
          {importResult.errors?.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs space-y-0.5">
              {importResult.errors.slice(0, 8).map((er, i) => <li key={i}>{er}</li>)}
              {importResult.errors.length > 8 && <li>…and {importResult.errors.length - 8} more</li>}
            </ul>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-sm w-fit">
        {[{k:"list",l:"Purchases"},{k:"report",l:"Reports"}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`} className={`px-4 py-2 text-sm font-semibold rounded-sm transition-all ${tab===t.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}>{t.l}</button>
        ))}
      </div>

      {tab === "list" && (
        <>
        <div className="mb-4 flex items-end gap-2 flex-wrap" data-testid="purchase-date-filters">
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500">Month</label>
            <input type="month" data-testid="purchase-month-filter" value={pMonth} onChange={(e) => { setPMonth(e.target.value); if (e.target.value) { setPFrom(""); setPTo(""); } }} className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500">Search Supplier / Bill No</label>
            <input
              type="text"
              data-testid="purchase-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="e.g. Janta Raja..."
              className="px-3 py-2 border border-slate-200 rounded-sm text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500">From</label>
            <input type="date" data-testid="purchase-from-filter" value={pFrom} onChange={(e) => { setPFrom(e.target.value); if (e.target.value) setPMonth(""); }} className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-slate-500">To</label>
            <input type="date" data-testid="purchase-to-filter" value={pTo} onChange={(e) => { setPTo(e.target.value); if (e.target.value) setPMonth(""); }} className="px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          {(pMonth || pFrom || pTo) && (
            <button data-testid="purchase-date-clear" onClick={() => { setPMonth(""); setPFrom(""); setPTo(""); }} className="px-3 py-2 text-sm font-semibold text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-100">Clear</button>
          )}
          <button
            data-testid="purchase-gstr-excel-btn"
            onClick={handleGstrExcel}
            title="Download GSTR-1 style Excel — Summary + B2B + HSN Summary + Party-wise sheets"
            className="px-3 py-2 border border-blue-600 text-blue-700 rounded-sm text-sm font-semibold hover:bg-blue-50 flex items-center gap-2"
          >
            <DownloadSimple size={18} weight="bold" /> Download GSTR-Style Excel
          </button>
          <button
            data-testid="purchase-tally-xml-btn"
            onClick={handleTallyXml}
            title="Download Tally-import XML — vouchers with supplier GSTIN, HSN, qty, rate, taxes (masters included)"
            className="px-3 py-2 border border-purple-600 text-purple-700 rounded-sm text-sm font-semibold hover:bg-purple-50 flex items-center gap-2"
          >
            <DownloadSimple size={18} weight="bold" /> Tally XML
          </button>
          {(pMonth || pFrom || pTo) && (
            <span className="text-xs font-semibold text-slate-500 pb-2">{visiblePurchases.length} of {purchases.length} shown</span>
          )}
        </div>

        {selIds.size > 0 && (
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-300 rounded-sm px-4 py-2.5" data-testid="purchase-bulk-bar">
            <span className="text-sm font-black text-blue-900">{selIds.size} bill{selIds.size > 1 ? "s" : ""} selected</span>
            <button
              data-testid="purchase-bulk-edit-btn"
              onClick={() => setBulkOpen(true)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-sm text-xs font-bold hover:bg-blue-700 flex items-center gap-1">
              <Pencil size={14} /> Edit Selected
            </button>
            <button
              data-testid="purchase-bulk-clear-btn"
              onClick={() => setSelIds(new Set())}
              className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-100">
              Clear Selection
            </button>
          </div>
        )}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="max-w-md" data-testid="purchase-bulk-dialog">
            <DialogHeader>
              <DialogTitle>Edit {selIds.size} Selected Bill{selIds.size > 1 ? "s" : ""}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                Only filled fields will change. Supplier name change re-links all bills to the correct party —
                Customer Ledger, stock ledger and godown entries update automatically.
              </p>
              <div>
                <Label>New Supplier Name (optional — existing parties only)</Label>
                {bulkForm.supplier_name ? (
                  <div className="flex items-center gap-2 mt-1 bg-emerald-50 border border-emerald-300 rounded-sm px-3 py-2" data-testid="bulk-supplier-chosen">
                    <span className="text-sm font-bold text-emerald-900">{bulkForm.supplier_name}</span>
                    <button type="button" data-testid="bulk-supplier-clear"
                      onClick={() => { setBulkForm({ ...bulkForm, supplier_name: "" }); setSupplierQuery(""); }}
                      className="ml-auto text-xs font-bold text-emerald-700 hover:text-rose-600">✕ Change</button>
                  </div>
                ) : (
                  <>
                    <Input
                      data-testid="bulk-supplier-name-input"
                      value={supplierQuery}
                      onChange={(e) => setSupplierQuery(e.target.value)}
                      placeholder="Search existing party..."
                      autoComplete="off"
                    />
                    {supplierQuery.trim() && (
                      <div className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="bulk-supplier-suggestions">
                        {suppliers
                          .filter((sp) => (sp.name || "").toLowerCase().includes(supplierQuery.trim().toLowerCase()))
                          .slice(0, 8)
                          .map((sp) => (
                            <button key={sp.id} type="button" data-testid={`bulk-supplier-option-${sp.id}`}
                              onClick={() => { setBulkForm({ ...bulkForm, supplier_name: sp.name }); setSupplierQuery(""); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50">
                              {sp.name}
                            </button>
                          ))}
                        {suppliers.filter((sp) => (sp.name || "").toLowerCase().includes(supplierQuery.trim().toLowerCase())).length === 0 && (
                          <p className="px-3 py-2 text-xs text-slate-400">No existing party matches — new names are not allowed here</p>
                        )}
                      </div>
                    )}
                    <p className="text-[11px] text-slate-400 mt-1">Sirf pehle se add kiye gaye parties me se hi select kar sakte hain.</p>
                  </>
                )}
              </div>
              <div>
                <Label>New Bill Date (optional)</Label>
                <Input
                  type="date"
                  data-testid="bulk-invoice-date-input"
                  value={bulkForm.invoice_date}
                  onChange={(e) => setBulkForm({ ...bulkForm, invoice_date: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-edit-cancel">Cancel</Button>
              <Button onClick={submitBulkEdit} disabled={bulkSaving} data-testid="bulk-edit-save" className="bg-blue-600 hover:bg-blue-700">
                {bulkSaving ? "Updating..." : `Update ${selIds.size} Bill${selIds.size > 1 ? "s" : ""}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="bg-white border border-slate-200 rounded-sm">
          {loading ? <div className="p-12 animate-pulse"><div className="h-40 bg-slate-200 rounded"></div></div> : visiblePurchases.length === 0 ? (
            <div className="p-12 text-center">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{purchases.length === 0 ? "No purchases yet" : "No purchases in the selected period"}</h3>
              {purchases.length === 0 && <p className="text-slate-500 mb-6">Record your first purchase invoice</p>}
            </div>
          ) : (
            <div className="overflow-x-auto sticky-table">
              <table className="w-full">
                <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3">
                      <input type="checkbox" data-testid="purchase-select-all" checked={allVisibleSelected}
                        onChange={toggleSelectAll} className="h-4 w-4 accent-blue-600 cursor-pointer" title="Select all shown bills" />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Purchase No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Inv No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Supplier</th>
                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Date</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Taxable</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">CGST</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">SGST</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">IGST</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {visiblePurchases.map(p => (
                    <tr key={p.id} className={`hover:bg-slate-50 ${selIds.has(p.id) ? "bg-blue-50/60" : ""}`}>
                      <td className="px-3 py-3">
                        <input type="checkbox" data-testid={`purchase-select-${p.id}`} checked={selIds.has(p.id)}
                          onChange={() => toggleSel(p.id)} className="h-4 w-4 accent-blue-600 cursor-pointer" />
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-900">{p.purchase_no}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{p.invoice_no}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-slate-900">
                          {p.supplier_name}
                          {p.customer_id && <span data-testid={`purchase-linked-${p.id}`} title="Linked to Customers master" className="ml-2 inline-block align-middle text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">Linked</span>}
                        </p>
                        {p.supplier_gstin && <p className="text-xs text-slate-500 font-mono">{p.supplier_gstin}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{format(new Date(p.invoice_date),"dd MMM yyyy")}</td>
                      <td className="px-4 py-3 text-sm text-right">{fmt(p.subtotal)}</td>
                      <td className="px-4 py-3 text-sm text-right text-blue-600">{fmt(p.cgst)}</td>
                      <td className="px-4 py-3 text-sm text-right text-blue-600">{fmt(p.sgst)}</td>
                      <td className="px-4 py-3 text-sm text-right text-purple-600">{fmt(p.igst)}</td>
                      <td className="px-4 py-3 text-sm font-bold text-right">{fmt(p.total)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => openNoteModal(p, "credit")}
                            data-testid={`purchase-cn-${p.id}`}
                            title="Create Credit Note (goods returned / discount received from supplier)"
                            className="px-2 py-1 text-[11px] font-bold rounded-sm border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          >+CN</button>
                          <button
                            onClick={() => openNoteModal(p, "debit")}
                            data-testid={`purchase-dn-${p.id}`}
                            title="Create Debit Note (goods returned to supplier)"
                            className="px-2 py-1 text-[11px] font-bold rounded-sm border border-rose-200 text-rose-700 hover:bg-rose-50"
                          >+DN</button>
                          <button onClick={() => handleEdit(p)} data-testid={`edit-purchase-${p.id}`} className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-sm"><Pencil size={16} /></button>
                          <button onClick={() => handleDelete(p.id)} data-testid={`delete-purchase-${p.id}`} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-sm"><Trash size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
      )}

      {tab === "report" && (
        <div className="space-y-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2" data-testid="purchases-fy-nav">
              <button onClick={() => setFyStart(fyStart-1)} title="Previous financial year" className="p-2 bg-white border border-slate-200 rounded-sm hover:bg-slate-50"><CaretLeft size={18} /></button>
              <div className="min-w-[160px] text-center">
                <p className="text-xl font-black leading-tight">FY {fyStart}-{String((fyStart + 1) % 100).padStart(2, "0")}</p>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Apr {fyStart} – Mar {fyStart + 1}</p>
              </div>
              <button onClick={() => setFyStart(fyStart+1)} title="Next financial year" className="p-2 bg-white border border-slate-200 rounded-sm hover:bg-slate-50"><CaretRight size={18} /></button>
            </div>
            <button onClick={() => handleExport("excel")} data-testid="export-purchases-excel" className="px-3 py-2 bg-green-600 text-white rounded-sm text-xs font-semibold hover:bg-green-700 flex items-center gap-1"><DownloadSimple size={16} /> Excel</button>
            <button onClick={() => handleExport("pdf")} data-testid="export-purchases-pdf" className="px-3 py-2 bg-blue-600 text-white rounded-sm text-xs font-semibold hover:bg-blue-700 flex items-center gap-1"><DownloadSimple size={16} /> PDF</button>
          </div>

          {summary && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white border border-slate-200 rounded-sm p-4"><p className="text-xs font-bold uppercase text-slate-500">Total Purchases</p><p className="text-xl font-black">{summary.totals?.count || 0}</p></div>
                <div className="bg-white border border-slate-200 rounded-sm p-4"><p className="text-xs font-bold uppercase text-slate-500">Taxable Value</p><p className="text-xl font-black">{fmt(summary.totals?.taxable)}</p></div>
                <div className="bg-white border border-slate-200 rounded-sm p-4"><p className="text-xs font-bold uppercase text-slate-500">Total GST</p><p className="text-xl font-black text-blue-600">{fmt(summary.totals?.total_gst)}</p></div>
                <div className="bg-white border border-slate-200 rounded-sm p-4"><p className="text-xs font-bold uppercase text-slate-500">Total Amount</p><p className="text-xl font-black">{fmt(summary.totals?.total)}</p></div>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto sticky-table">
                <table className="w-full">
                  <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Month</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">Count</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">Taxable</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">CGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">SGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">IGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">Export</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {summary.months?.map(m => (
                      <tr key={m.month} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-semibold">{m.month_name}</td>
                        <td className="px-4 py-3 text-sm text-right">{m.count}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(m.taxable)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(m.cgst)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(m.sgst)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(m.igst)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-right">{fmt(m.total)}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => handleExport("excel", m.month)} className="text-xs text-green-600 font-semibold hover:underline mr-2">XLS</button>
                          <button onClick={() => handleExport("pdf", m.month)} className="text-xs text-blue-600 font-semibold hover:underline">PDF</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          aria-describedby={undefined}
          onInteractOutside={(e) => {
            const t = e.detail?.originalEvent?.target || e.target;
            if (t?.closest?.('[data-group-picker-panel]')) e.preventDefault();
          }}
        >
          <DialogHeader><DialogTitle className="text-xl font-bold">{editing ? "Edit" : "Add"} Purchase</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Supplier / Purchase Client *</Label>
                <CustomerCombobox
                  customers={suppliers}
                  partyType="purchase"
                  value={selectedSupplierId}
                  onSelect={(s) => {
                    setSelectedSupplierId(s.id);
                    setForm((f) => ({
                      ...f,
                      supplier_name: s.name,
                      supplier_gstin: s.gstin || "",
                      supplier_address: s.address || "",
                      supplier_state: s.state || f.supplier_state,
                      supplier_state_code: s.state_code || f.supplier_state_code,
                    }));
                  }}
                  onCreated={(c) => setSuppliers((prev) => [...prev, c].sort((a, b) => a.name.localeCompare(b.name)))}
                  testId="purchase-supplier-picker"
                />
              </div>
              <div><Label>{fcfg.label("supplier_name", "Supplier Name")} *</Label><Input data-testid="purchase-supplier" autoCap value={form.supplier_name} onChange={e => setForm({...form, supplier_name: e.target.value})} disabled={!fcfg.canEdit("supplier_name")} required /></div>
              <div>
                <Label>Supplier GSTIN</Label>
                <Input data-testid="purchase-gstin" value={form.supplier_gstin} maxLength={15} placeholder="15-digit GSTIN auto-fetches" onChange={e => onSupplierGstinChange(e.target.value)} />
                {gstinMsg && <p data-testid="purchase-gstin-status" className={`mt-1 text-[11px] font-semibold ${gstinMsg.tone}`}>{gstinMsg.text}</p>}
              </div>
              <div className="sm:col-span-2"><Label>Address</Label><Input autoCap value={form.supplier_address} onChange={e => setForm({...form, supplier_address: e.target.value})} /></div>
              <div><Label>State</Label><select value={form.supplier_state} onChange={e => handleStateChange(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm">{STATES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              <div><Label>Invoice No *</Label><Input data-testid="purchase-inv-no" value={form.invoice_no} onChange={e => setForm({...form, invoice_no: e.target.value})} required /></div>
              <div><Label>Invoice Date *</Label><Input type="date" data-testid="purchase-date" value={form.invoice_date} onChange={e => setForm({...form, invoice_date: e.target.value})} required /></div>
              <div><Label>GST Type *</Label><select data-testid="purchase-gst-type" value={form.gst_type} onChange={e => setForm({...form, gst_type: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm font-semibold"><option value="cgst_sgst">CGST + SGST</option><option value="igst">IGST</option><option value="no_gst">No GST / Nil Rated</option></select></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3"><Label>Items</Label><button type="button" onClick={addItem} data-testid="purchase-add-item" className="px-2 py-1 bg-blue-600 text-white rounded-sm text-xs font-semibold flex items-center gap-1"><Plus size={14} /> Add</button></div>
              {items.map((item, idx) => (
                <div key={idx} className="border border-slate-100 rounded-sm p-2 mb-2 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ProductPicker
                      products={products}
                      value={item.product_id || ""}
                      onChange={(p) => applyProduct(idx, p)}
                      onProductCreated={(p) => setProducts((prev) => [...prev, p])}
                      testid={`purchase-item-product-${idx}`}
                    />
                    <Input className="flex-1 min-w-[120px]" autoCap value={item.description} onChange={e => updateItem(idx, "description", e.target.value)} placeholder="Description" required />
                    <Input className="w-20" value={item.hsn_sac} onChange={e => updateItem(idx, "hsn_sac", e.target.value)} placeholder="HSN" />
                    <Input type="number" className="w-16" step="0.01" min="0.01" value={item.quantity} onChange={e => updateItem(idx, "quantity", parseFloat(e.target.value)||0)} />
                    <Input type="number" className="w-20" step="0.01" value={item.price} onChange={e => updateItem(idx, "price", parseFloat(e.target.value)||0)} placeholder="Price" />
                    <select className="w-16 px-1 py-1.5 border border-slate-200 rounded-sm text-xs" value={item.gst_rate} onChange={e => updateItem(idx, "gst_rate", parseFloat(e.target.value))}>{GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}</select>
                    <span className="text-sm text-slate-500 min-w-[70px] text-right">{"\u20b9"}{item.amount.toFixed(2)}</span>
                    <button type="button" onClick={() => removeItem(idx)} className="p-1 text-red-500"><Trash size={16} /></button>
                  </div>
                  {item.product_id && (() => {
                    const p = products.find((x) => x.id === item.product_id);
                    if (!p) return null;
                    const newStock = (p.current_stock ?? 0) + (parseFloat(item.quantity) || 0);
                    return (
                      <div className="text-[11px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <span>↑</span>
                        <span>Stock will rise to <b>{newStock} {p.unit || "PCS"}</b> on save</span>
                      </div>
                    );
                  })()}
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Add at least one item</p>}
            </div>
            <div><Label>Notes</Label><Textarea autoCap value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} /></div>
            {/* Godown option — decide whether purchased goods go into a godown */}
            <div className="border border-slate-200 rounded-sm p-3 bg-amber-50/40 space-y-2" data-testid="purchase-godown-section">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.to_godown} data-testid="purchase-to-godown-toggle"
                  onChange={(e) => setForm({ ...form, to_godown: e.target.checked })} className="w-4 h-4" />
                <span className="text-sm font-bold text-slate-800">Send purchased goods to Godown (stock-in record)</span>
              </label>
              {form.to_godown && (
                <div className="flex gap-2 flex-wrap items-end pl-6">
                  <div>
                    <Label className="text-xs">Choose Godown</Label>
                    <select value={form.godown_id} data-testid="purchase-godown-select"
                      onChange={(e) => { const g = godowns.find((x) => x.id === e.target.value); setForm({ ...form, godown_id: e.target.value, godown_name: g ? g.name : form.godown_name }); }}
                      className="h-9 px-3 border border-slate-200 rounded-sm text-sm font-semibold block min-w-[180px]">
                      <option value="">— New / type name →</option>
                      {godowns.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                  {!form.godown_id && (
                    <div>
                      <Label className="text-xs">Godown Name (auto-created)</Label>
                      <Input value={form.godown_name} data-testid="purchase-godown-name-input"
                        onChange={(e) => setForm({ ...form, godown_name: e.target.value })} placeholder="Main Godown" className="w-48" />
                    </div>
                  )}
                </div>
              )}
            </div>
            {items.length > 0 && (
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-1" data-testid="purchase-totals">
                <div className="flex justify-between text-sm"><span className="text-slate-600">Taxable Value</span><span className="font-semibold">{"\u20b9"}{items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0).toFixed(2)}</span></div>
                <GstBreakdown items={items} gstType={form.gst_type} />
                <div className="flex justify-between text-sm font-black border-t border-slate-300 pt-1">
                  <span>Total</span>
                  <span data-testid="purchase-total-amount">{"\u20b9"}{(items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0) + (form.gst_type === "no_gst" ? 0 : items.reduce((s, it) => s + ((parseFloat(it.amount) || 0) * (parseFloat(it.gst_rate) || 0)) / 100, 0))).toFixed(2)}</span>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="save-purchase-btn" title="Ctrl+Enter to save">{editing ? "Update" : "Save"} Purchase</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- Debit / Credit Note modal ------------------------------------ */}
      <Dialog open={!!noteModal} onOpenChange={(v) => !v && setNoteModal(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="purchase-note-modal">
          <DialogHeader>
            <DialogTitle>
              {noteModal?.noteType === "debit" ? "Create Debit Note" : "Create Credit Note"}
              <span className="ml-2 text-xs font-normal text-slate-500">
                against {noteModal?.purchase?.purchase_no} · {noteModal?.purchase?.supplier_name}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Note Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={noteForm.note_date} onChange={(e) => setNoteForm({ ...noteForm, note_date: e.target.value })} data-testid="note-date-input" />
              </div>
              <div>
                <Label>GST Type</Label>
                <Input value={noteModal?.purchase?.gst_type === "cgst_sgst" ? "CGST + SGST (Intra-state)" : "IGST (Inter-state)"} disabled />
              </div>
            </div>
            <div>
              <Label>Reason <span className="text-red-500">*</span></Label>
              <Input value={noteForm.reason} onChange={(e) => setNoteForm({ ...noteForm, reason: e.target.value })} data-testid="note-reason-input" placeholder="e.g., 5 units damaged in transit" />
            </div>

            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-bold uppercase tracking-wider">Adjusted Items</Label>
                <p className="text-[11px] text-slate-500">Edit qty/price to reflect the exact adjustment. Stock ledger will auto-reduce.</p>
              </div>
              <div className="space-y-2">
                {noteForm.items.map((it, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2">
                    <Input className="flex-1 min-w-[180px]" value={it.product_name} onChange={(e) => updateNoteItem(idx, "product_name", e.target.value)} placeholder="Item description" />
                    <Input className="w-20" value={it.hsn_sac} onChange={(e) => updateNoteItem(idx, "hsn_sac", e.target.value)} placeholder="HSN" />
                    <Input className="w-16" type="number" step="0.01" min="0" value={it.quantity} onChange={(e) => updateNoteItem(idx, "quantity", e.target.value)} title="Quantity being adjusted" data-testid={`note-item-qty-${idx}`} />
                    <Input
                      className="w-16"
                      value={it.unit || "PCS"}
                      onChange={(e) => updateNoteItem(idx, "unit", e.target.value.toUpperCase())}
                      title="Unit of measure (KG / PCS / LTR / MTR / …) — carried from source bill"
                      data-testid={`note-item-unit-${idx}`}
                    />
                    <Input className="w-20" type="number" step="0.01" min="0" value={it.price} onChange={(e) => updateNoteItem(idx, "price", e.target.value)} title="Unit price" />
                    <select className="w-16 px-1 py-1.5 border border-slate-200 rounded-sm text-xs" value={it.gst_rate} onChange={(e) => updateNoteItem(idx, "gst_rate", e.target.value)}>
                      {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
                    </select>
                    <span className="text-sm text-slate-600 min-w-[70px] text-right">₹{Number(it.amount || 0).toFixed(2)}</span>
                    <button type="button" onClick={() => removeNoteItem(idx)} className="p-1 text-red-500 hover:bg-red-50 rounded-sm"><Trash size={16} /></button>
                  </div>
                ))}
                {noteForm.items.length === 0 && (
                  <p className="text-xs text-slate-500 italic text-center py-4">No items — add via source purchase or type manually.</p>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-5 gap-2 text-xs">
                <div className="text-slate-500"><div className="font-semibold">Taxable</div><div className="text-sm font-bold text-slate-900">₹{noteTotals.taxable.toFixed(2)}</div></div>
                <div className="text-slate-500"><div className="font-semibold">CGST</div><div className="text-sm font-bold text-slate-900">₹{noteTotals.cgst.toFixed(2)}</div></div>
                <div className="text-slate-500"><div className="font-semibold">SGST</div><div className="text-sm font-bold text-slate-900">₹{noteTotals.sgst.toFixed(2)}</div></div>
                <div className="text-slate-500"><div className="font-semibold">IGST</div><div className="text-sm font-bold text-slate-900">₹{noteTotals.igst.toFixed(2)}</div></div>
                <div className="text-slate-500"><div className="font-semibold">Total</div><div className="text-base font-black text-emerald-700">₹{noteTotals.total.toFixed(2)}</div></div>
              </div>
            </div>

            <div>
              <Label>Additional Notes (optional)</Label>
              <Textarea value={noteForm.notes} onChange={(e) => setNoteForm({ ...noteForm, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setNoteModal(null)}>Cancel</Button>
            <Button
              type="button"
              onClick={submitNote}
              disabled={noteSaving}
              data-testid="save-purchase-note-btn"
              className={noteModal?.noteType === "debit" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}
            >
              {noteSaving ? "Saving…" : `Save ${noteModal?.noteType === "debit" ? "Debit" : "Credit"} Note`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
